import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  Inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IAddressSuggestion, AddressSearch, IAddress } from '@nx-ng/types';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { AddressesStoreActions } from '../../../addresses-store';
import { takeUntil } from 'rxjs/operators';
import { AddressSearchResponse } from '@modules/addresses-store/client/src';
import { formatAddressSearchResults } from './format-address-search-results';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

@Component({
  selector: 'nx-ng-postcode',
  templateUrl: './postcode.component.html',
  styleUrls: ['./postcode.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PostcodeComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PostcodeComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  private unsubscribe$ = new Subject<void>();

  @Input()
  id = '';

  @Input()
  label = '';

  @Input()
  errorText = '';

  @Input()
  placeholder = '';

  @Input()
  isRequired = false;

  @Input()
  addressSearchContext = '';

  selectedAddressValue = '';

  private _addressSuggestions: IAddressSuggestion[] = [];
  set addressSuggestions(value: IAddressSuggestion[]) {
    this._addressSuggestions = value;
    if (value && value.length > 0) {
      this.suggestionsVisible = true;

      setTimeout(() => {
        this.focusOnPostcodeText();
      }, 0);
      this.mobileSuggestionsVisible = true;
    }
  }
  get addressSuggestions(): IAddressSuggestion[] {
    return this._addressSuggestions;
  }

  @Output()
  addressSearch: EventEmitter<AddressSearch> = new EventEmitter<AddressSearch>();

  @Output()
  addressFound: EventEmitter<IAddress> = new EventEmitter<IAddress>();

  @ViewChildren('postcodeItem')
  dropdownItems: QueryList<ElementRef> | undefined;

  @ViewChild('postcodeInput')
  postcodeInput: ElementRef | undefined;

  private localValue = '';
  private touched = false;

  suggestionsVisible = false;

  mobileSuggestionsVisible = false;

  activeItem = -1;

  isLoading = false;

  hasSearchError = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private actions$: Actions,
    @Inject(I18NEXT_SERVICE) private i18n: ITranslationService
  ) {}

  /* Note: this component has a unique id that is used to make requests to the
     address search service. This unique id is a key from the parent component
     plus a unique local uuid. This allows us to have more than one address
     search on the page at one time. */
  ngOnInit(): void {
    this.actions$
      .pipe(
        ofType(AddressesStoreActions.addressAPI_retrieveSuccess),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ address, requester }) => {
        if (requester === this.addressSearchId) {
          this.isLoading = false;
          this.addressFound.emit(address);
          this.handleListClose();
        }
      });

    this.actions$
      .pipe(
        ofType(
          AddressesStoreActions.addressAPI_searchSuccess,
          AddressesStoreActions.addressAPI_drillDownSuccess
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        ({
          searchResponse,
          requester,
        }: {
          searchResponse: AddressSearchResponse;
          requester: string;
        }) => {
          if (requester !== this.addressSearchId) {
            return;
          }
          this.isLoading = false;
          this.addressSuggestions = formatAddressSearchResults(searchResponse);
          if (this.addressSuggestions.length === 0) {
            this.hasSearchError = true;
          } else {
            this.hasSearchError = false;
          }
        }
      );

    this.actions$
      .pipe(
        ofType(AddressesStoreActions.addressAPI_retrieve),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ requester }) => {
        if (requester === this.addressSearchId) {
          this.isLoading = true;
        }
      });

    this.actions$
      .pipe(
        ofType(AddressesStoreActions.addressAPI_search),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ requester }) => {
        if (requester === this.addressSearchId) {
          this.isLoading = true;
        }
      });

    this.actions$
      .pipe(
        ofType(AddressesStoreActions.addressAPI_drillDown),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ requester }) => {
        if (requester === this.addressSearchId) {
          this.isLoading = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  set value(value: string) {
    this.localValue = value;
    this.onChange(value.replace(/-/g, ''));
  }

  get value(): string {
    return `${this.localValue}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (event: any) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouch = () => {};

  writeValue(value: string) {
    this.localValue = value;

    if (this.changeDetectorRef) {
      this.changeDetectorRef.markForCheck();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  markAsTouched() {
    this.suggestionsVisible = false;
    if (!this.touched) {
      this.touched = true;
      this.onTouch();
    }
  }

  get selectPlaceholderText() {
    return this.selectedAddressValue
      ? this.i18n.t('postcode_result_select_address_from', {
          ns: 'translation',
          replace: {
            container: this.selectedAddressValue,
          },
        })
      : this.i18n.t('postcode_result_select_address', {
          ns: 'translation',
        });
  }

  get errorId() {
    return `${this.id}-error`;
  }

  get hasError() {
    return this.errorText?.length > 0;
  }

  get controlsId() {
    return `${this.id}-controls`;
  }

  get itemsId() {
    return `${this.id}-lookup`;
  }

  get addressSearchId() {
    return `${this.addressSearchContext}`;
  }

  getSuggestionId(item: string) {
    return `${this.itemsId}-suggestion-${item}`;
  }

  handleListClose() {
    this.activeItem = 0;
    this.suggestionsVisible = false;
  }

  handleSelectClick(value?: string): void {
    if (!value) return;

    const actual = this.addressSuggestions.find((s) => s.id === value);

    this.selectedAddressValue = actual?.label || '';

    if (!actual) return;

    if (actual.isContainer) {
      this.addressSearch.emit({
        operation: 'drilldown',
        id: actual.id,
        requester: this.addressSearchId,
      });
    }
    if (actual.containerSize === 1) {
      this.addressSearch.emit({
        operation: 'retrieve',
        id: actual.id,
        requester: this.addressSearchId,
      });
    }
    this.handleListClose();
  }

  getItemElement(index: number) {
    if (!this.dropdownItems) return;
    return this.dropdownItems.get(index)?.nativeElement as HTMLElement;
  }

  focusOnSuggestion(index: number) {
    if (!this.dropdownItems || index > this.dropdownItems.length - 1) return;
    const element = this.getItemElement(index);
    this.activeItem = index;
    setTimeout(() => element?.focus(), 0);
  }

  focusOnNextSuggestion() {
    if (!this.dropdownItems) return;

    this.activeItem =
      this.activeItem == this.dropdownItems.length - 1
        ? 0
        : this.activeItem + 1;

    this.focusOnSuggestion(this.activeItem);
  }

  focusOnPrevSuggestion() {
    if (!this.dropdownItems) return;
    this.activeItem =
      this.activeItem == 0
        ? this.dropdownItems.length - 1
        : this.activeItem - 1;

    this.focusOnSuggestion(this.activeItem);
  }

  getItemElementValue(index: number) {
    const element = this.getItemElement(index);
    return element?.querySelector('p')?.innerText;
  }

  focusLastSuggestion() {
    this.dropdownItems
      ? this.focusOnSuggestion(this.dropdownItems.length - 1)
      : this.focusOnSuggestion(0);
  }

  focusFirstSuggestion() {
    this.focusOnSuggestion(0);
  }

  focusOnPostcodeText() {
    const postcode = this.postcodeInput?.nativeElement as HTMLInputElement;
    if (postcode) {
      postcode.focus();
    }
  }

  formatAddressForSelect(address: IAddressSuggestion) {
    const addressLabel =
      address.containerSize > 1
        ? `${address.label} (${this.i18n.t('postcode_result_addresses', {
            ns: 'translation',
            replace: {
              count: address.containerSize,
            },
          })})`
        : `${address.label} (${this.i18n.t('postcode_result_single_address', {
            ns: 'translation',
          })})`;

    return addressLabel;
  }

  handleSearchInput(event: string) {
    this.hasSearchError = false;
    this.addressSearch.emit({
      operation: 'search',
      fragment: event,
      requester: this.addressSearchId,
    });
  }

  handlePostcodeKeyEvent(event: KeyboardEvent) {
    switch (event.code) {
      case 'Enter':
      case 'ArrowDown':
        if (this.addressSuggestions.length > 0) {
          this.focusFirstSuggestion();
          this.suggestionsVisible = true;
        }
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (this.addressSuggestions.length > 0) {
          this.suggestionsVisible = true;
          this.focusLastSuggestion();
        }
        event.preventDefault();
        break;
      case 'Tab':
        break;
      default:
        this.selectedAddressValue = '';
        break;
    }
  }

  handleDropdownKeyEvent(addressId: string, event: KeyboardEvent) {
    switch (event.code) {
      case 'ArrowDown':
        this.focusOnNextSuggestion();
        break;
      case 'ArrowUp':
        this.focusOnPrevSuggestion();
        break;
      case 'Enter':
      case 'Space':
        this.handleSelectClick(addressId);
        break;
      case 'Tab':
        this.handleListClose();
        break;
      case 'Escape':
        this.handleListClose();
        this.focusOnPostcodeText();
        break;
      case 'Home':
        this.focusFirstSuggestion();
        break;
      case 'End':
        this.focusLastSuggestion();
        break;
    }

    if (event.key > '0' && event.key <= '9') {
      this.focusOnSuggestion(Number(event.key));
    } else if (event.key == 'r') {
      this.focusOnSuggestion(0);
    }
    event.preventDefault();
  }

  handleInputBlur() {
    setTimeout(() => {
      const element = document?.activeElement as HTMLInputElement;
      if (!element?.id.includes('suggestion') && !this.isLoading) {
        this.markAsTouched();
      }
    }, 100);
  }

  handleInputFocus() {
    if (this.addressSuggestions.length > 0) {
      this.suggestionsVisible = true;
    }
  }

  handleSuggestionsBlur() {
    setTimeout(() => {
      const element = document?.activeElement as HTMLInputElement;
      if (
        element?.id !== this.id &&
        !element?.id?.includes('suggestion') &&
        !element?.id?.includes('postcode-lookup') &&
        !this.isLoading
      ) {
        this.markAsTouched();
      }
    });
  }

  trackByFn(_index: number, item: IAddressSuggestion) {
    return item.id;
  }
}
